import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { LOGO_URL, COMPANY_INFO, COVERAGE_AREAS, SOCIAL_MEDIA } from '../../data/mockData';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="relative h-12 w-12 flex-shrink-0 rounded-full overflow-hidden bg-black ring-1 ring-white/10">
                <img
                  src={LOGO_URL}
                  alt="Alexander Conti — Brantford Area Homes"
                  className="absolute inset-0 h-full w-full object-cover scale-[1.6]"
                />
              </span>
              <div>
                <h3 className="font-bold text-lg">ALEXANDER CONTI</h3>
                <p className="text-red-500 text-xs italic">Your Vision, My Mission</p>
                <p className="text-gray-400 text-xs mt-0.5">{COMPANY_INFO.brokerage}</p>
              </div>
            </div>
            <div className="space-y-3 text-gray-400">
              <a href={`tel:${COMPANY_INFO.phone}`} className="flex items-center gap-2 hover:text-red-500 transition-colors">
                <Phone size={16} />
                {COMPANY_INFO.phone}
              </a>
              <Link
                to="/contact"
                className="group flex items-start gap-2 hover:text-red-500 transition-colors"
                data-testid="footer-email"
              >
                <Mail size={16} className="mt-0.5 flex-shrink-0" />
                <span className="flex flex-col leading-tight min-w-0">
                  <span className="font-medium text-gray-200 group-hover:text-red-500 transition-colors">Email me</span>
                  <span className="text-[11px] text-gray-500 group-hover:text-red-400/80 transition-colors [overflow-wrap:anywhere]">
                    alexanderconti<wbr />@brantfordareahomes.ca
                  </span>
                </span>
              </Link>
              <div className="flex items-start gap-2">
                <MapPin size={16} className="mt-1 flex-shrink-0" />
                {COMPANY_INFO.address}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4 text-red-500">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/search" className="text-gray-400 hover:text-white transition-colors">Search Properties</Link></li>
              <li><Link to="/sell" className="text-gray-400 hover:text-white transition-colors">Sell Your Home</Link></li>
              <li><Link to="/mortgage-calculator" className="text-gray-400 hover:text-white transition-colors">Mortgage Calculator</Link></li>
              <li><Link to="/neighbourhoods" className="text-gray-400 hover:text-white transition-colors">Neighbourhood Guides</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Coverage Areas */}
          <div>
            <h4 className="font-semibold text-lg mb-4 text-red-500">Service Areas</h4>
            <ul className="space-y-2 text-gray-400">
              {COVERAGE_AREAS.slice(0, 8).map((area) => (
                <li key={area.id}>
                  <Link to={`/neighbourhoods/${area.id}`} className="hover:text-white transition-colors">
                    {area.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/neighbourhoods" className="text-red-500 hover:text-red-400 transition-colors">
                  View All Areas →
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter / Social */}
          <div>
            <h4 className="font-semibold text-lg mb-4 text-red-500">Connect With Us</h4>
            <p className="text-gray-400 mb-4">Follow us on social media for the latest listings and market updates.</p>
            <div className="flex gap-4">
              <a href={SOCIAL_MEDIA.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-red-600 transition-colors" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href={SOCIAL_MEDIA.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-red-600 transition-colors" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Royal LePage Branding */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-6">
            <div className="bg-white p-2 rounded">
              <img 
                src="/brand/footer.jpeg" 
                alt="Royal LePage Brant Realty, Brokerage" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <span className="text-gray-400 text-sm text-center">
              Independently Owned and Operated | Royal LePage Brant Realty, Brokerage
            </span>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>© {new Date().getFullYear()} Alexander Conti | {COMPANY_INFO.brokerage}. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-4 text-center">
            The trademarks REALTOR®, REALTORS®, and the REALTOR® logo are controlled by The Canadian Real Estate Association (CREA) and identify real estate professionals who are members of CREA.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
